import { GRID_CONFIG, sortByAuditUpdatedOn } from "../../config";
import axios from "axios";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import {
    FaSearch,
    FaTimes,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaArrowRight,
    FaSyncAlt,
} from "react-icons/fa";

import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";

const InvestmentTable = ({ user }) => {
    InvestmentTable.propTypes = {
        user: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            email: PropTypes.string,
            username: PropTypes.string,
        }),
    };

    // If there are other components (like StackedHeader) that use 'text' prop:
    // Add prop-types for them as well
    // Example:
    // StackedHeader.propTypes = {
    //     text: PropTypes.string.isRequired,
    // };
    const [investments, setInvestments] = useState([]);
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [cancellingInvestment, setCancellingInvestment] = useState(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [selectedInvestment, setSelectedInvestment] = useState(null);
    const [cancellationReason, setCancellationReason] = useState("");
    const [cancellationNotes, setCancellationNotes] = useState("");
    const [refreshingOrderStatusId, setRefreshingOrderStatusId] =
        useState(null);
    const [reconfirmingInvestmentId, setReconfirmingInvestmentId] =
        useState(null);
    const [currentTime, setCurrentTime] = useState(Date.now());

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const deadlineTimer = setInterval(() => {
            setCurrentTime(Date.now());
        }, 30000);

        return () => clearInterval(deadlineTimer);
    }, []);

    // Fetch investments data function - made reusable
    const fetchInvestments = async () => {
        try {
            const userId = user?.id;

            if(!userId) {
                console.error("User ID not available");
                return;
            }

            // Add user_id as query parameter to fetch only this user's investments
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/investments`,
                {
                    params: { user_id: userId },
                    withCredentials: true,
                },
            );
            // Debug: Log the data to see what's actually returned
            console.log("Investment data from backend:", res.data);
            console.log(
                "Available fields in first item:",
                Object.keys(res.data[0] || {}),
            );

            // Transform backend response into the expected format
            const formatted = res.data.map((item) => ({
                id: item.id,
                name: item.campaign_name,
                date: new Date(item.created_at).toLocaleDateString(),
                created_at: item.created_at,
                amount: parseFloat(item.amount),
                raised: item.total_raised ? parseFloat(item.total_raised) : 0,
                percentRaised: item.percentage_raised || 0,
                daysRemaining: item.days_remaining || 0,
                shareLink: "#",
                // Try different possible field names for tradeId
                tradeId:
                    item.nc_trade_id ||
                    item.trade_id ||
                    item.tradeId ||
                    item.transaction_id ||
                    item.transactionId,
                // Use cancelled field from database
                cancelled: item.cancelled,
                cancelledBy:
                    item.cancelled_by_email ||
                    (item.cancelled_by
                        ? `User ID: ${item.cancelled_by}`
                        : "Unknown"),
                cancelledAt: item.cancelled_at,
                cancellationReason: item.cancellation_reason,
                cancellationNotes: item.cancellation_notes,
                orderStatus:
                    item.orderStatus ||
                    item.order_status ||
                    item.nc_order_status ||
                    item.payment_status ||
                    "-",
                paymentStatus: item.payment_status,
                reconfirmationRequired: Boolean(
                    item.reconfirmation_required,
                ),
                reconfirmationExpired: Boolean(
                    item.reconfirmation_expired,
                ),
                reconfirmationDeadline: item.reconfirmation_deadline,
            }));

            setInvestments(sortByAuditUpdatedOn(formatted));
        } catch(err) {
            console.error("Error fetching investments:", err);
        }
    };

    useEffect(() => {
        fetchInvestments();
    }, [user]);

    const handleReconfirmInvestment = async (investment) => {
        setReconfirmingInvestmentId(investment.id);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/investments/${investment.id}/reconfirm`,
                {},
                { withCredentials: true },
            );

            setInvestments((currentInvestments) =>
                currentInvestments.map((currentInvestment) =>
                    currentInvestment.id === investment.id
                        ? {
                              ...currentInvestment,
                              reconfirmationRequired: false,
                              reconfirmationExpired: false,
                          }
                        : currentInvestment,
                ),
            );
            showSuccessToast(
                response.data?.message ||
                    "Investment reconfirmed successfully.",
            );
        } catch(error) {
            showErrorToast(
                error.response?.data?.error ||
                    "Failed to reconfirm investment.",
            );

            if(
                error.response?.status === 409 ||
                error.response?.status === 410
            ) {
                setInvestments((currentInvestments) =>
                    currentInvestments.map((currentInvestment) =>
                        currentInvestment.id === investment.id
                            ? {
                                  ...currentInvestment,
                                  reconfirmationRequired: false,
                                  reconfirmationExpired:
                                      error.response?.status === 410,
                              }
                            : currentInvestment,
                    ),
                );
            }
        } finally {
            setReconfirmingInvestmentId(null);
        }
    };

    // Helper function to check if investment is within 48 hours
    const isWithin48Hours = (createdAt) => {
        if(!createdAt) return false;

        const createdTime = new Date(createdAt).getTime();
        const currentTime = new Date().getTime();
        const hoursDifference = (currentTime - createdTime) / (1000 * 60 * 60);

        return hoursDifference <= 48;
    };

    // Handle manual investment cancellation (for investments without North Capital trade IDs)
    const handleManualCancellation = async (investment) => {
        setCancellingInvestment(investment.id);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/investments/cancel-manual`,
                {
                    investmentId: investment.id,
                    reason: cancellationReason || "User requested cancellation",
                    notes:
                        cancellationNotes ||
                        `Manual cancellation for ${investment.name} investment of $${investment.amount}`,
                },
                { withCredentials: true },
            );

            if(response.data.success) {
                showSuccessToast(response.data.message);
                // Refresh investments list efficiently
                await fetchInvestments();
            } else {
                showErrorToast(
                    "Failed to cancel investment: " +
                        (response.data.message || "Unknown error"),
                );
            }
        } catch(error) {
            console.error("Error cancelling manual investment:", error);
            showErrorToast(
                "Error cancelling investment: " +
                    (error.response?.data?.message || error.message),
            );
        } finally {
            setCancellingInvestment(null);
            setShowCancelDialog(false);
            setSelectedInvestment(null);
            setCancellationReason("");
            setCancellationNotes("");
        }
    };

    // Cancel North Capital investment function
    const handleCancelInvestment = async (investment) => {
        setCancellingInvestment(investment.id);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/cancel-investment`,
                {
                    tradeId: investment.tradeId,
                    requestedBy: user?.email || user?.username || "User",
                    reason: cancellationReason || "User requested cancellation",
                    notes:
                        cancellationNotes ||
                        `Cancellation requested for ${investment.name} investment of $${investment.amount}`,
                },
                {
                    withCredentials: true,
                },
            );

            console.log("Cancel investment response:", response.data);

            if(
                response.data.success ||
                response.data.message?.includes("successfully")
            ) {
                showSuccessToast(
                    "North Capital investment cancelled successfully!",
                );
                // Refresh investments list efficiently
                await fetchInvestments();
            } else {
                showErrorToast(
                    "Failed to cancel investment: " +
                        (response.data.message || "Unknown error"),
                );
            }
        } catch(error) {
            console.error("Error cancelling investment:", error);
            showErrorToast(
                "Error cancelling investment: " +
                    (error.response?.data?.message || error.message),
            );
        } finally {
            setCancellingInvestment(null);
            setShowCancelDialog(false);
            setSelectedInvestment(null);
            setCancellationReason("");
            setCancellationNotes("");
        }
    };

    // Show confirmation dialog
    const showCancelConfirmation = async (investment) => {
        // Check if investment is within 48 hours
        if(!isWithin48Hours(investment.created_at)) {
            showErrorToast(
                "Investment can only be cancelled within 48 hours of creation. This investment was created more than 48 hours ago.",
            );
            return;
        }

        try {
            const statusRes = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/investor-info`,
                { params: { email: user?.email }, withCredentials: true },
            );
            const investorStatus = statusRes.data?.status;

            if(investorStatus !== "approved") {
                showErrorToast(
                    `Your account status is currently "${investorStatus || "unknown"}". Only approved accounts can cancel investments.`,
                );
                return;
            }
        } catch(err) {
            console.error("Error checking investor status:", err);
            showErrorToast("Unable to verify account status. Please try again.");
            return;
        }

        setSelectedInvestment(investment);
        setShowCancelDialog(true);
    };

    // Handle cancel confirmation - smart detection of investment type
    const confirmCancelInvestment = () => {
        if(selectedInvestment) {
            // Close the modal first
            setShowCancelDialog(false);

            // Check if this is a North Capital trade or manual investment
            if(
                selectedInvestment.tradeId &&
                !selectedInvestment.tradeId.startsWith("INV-")
            ) {
                // North Capital trade - use the API
                handleCancelInvestment(selectedInvestment);
            } else {
                // Manual investment - use local cancellation
                handleManualCancellation(selectedInvestment);
            }
        }
    };

    // Handle cancel dialog close
    const closeCancelDialog = () => {
        setShowCancelDialog(false);
        setSelectedInvestment(null);
        setCancellationReason("");
        setCancellationNotes("");
    };

    const handleRefreshOrderStatus = async (investment) => {
        if(!investment?.tradeId) {
            showErrorToast("Trade ID not available for this investment");
            return;
        }

        setRefreshingOrderStatusId(investment.id);

        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/trading/get-trade-status`,
                {
                    tradeId: investment.tradeId,
                },
                {
                    withCredentials: true,
                },
            );

            await fetchInvestments();
        } catch(error) {
            console.error("Error refreshing order status:", error);
            showErrorToast(
                "Error refreshing order status: " +
                    (error.response?.data?.message || error.message),
            );
        } finally {
            setRefreshingOrderStatusId(null);
        }
    };

    // Custom styles
    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "13px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "45px",
                "&:hover": { backgroundColor: "#f1f8ff" },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    const StackedHeader = ({ text }) => {
        const words = text.split(" ");
        const firstLine = words.slice(0, 2).join(" ");
        const secondLine = words.slice(2).join(" ");
        return (
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <span>{firstLine}</span>
                <span>{secondLine}</span>
            </div>
        );
    };

    StackedHeader.propTypes = {
        text: PropTypes.string.isRequired,
    };

    const useScrollableColumns = windowWidth < 1024;
    const scrollableTableWidth = "1030px";

    // Table columns
    const columns = [
        {
            name: "CAMPAIGN NAME",
            selector: (row) => row.name,
            sortable: true,
            width: useScrollableColumns ? "180px" : "12%",
            minWidth: useScrollableColumns ? "180px" : "auto",
            cell: (row) => (
                <div
                    style={{
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        color: "var(--navy)",
                        fontWeight: 500,
                    }}
                    title={row.name}
                >
                    {row.name}
                </div>
            ),
        },
        {
            name: "DATE",
            selector: (row) => row.date,
            sortable: true,
            width: useScrollableColumns ? "110px" : "9%",
            minWidth: useScrollableColumns ? "110px" : "auto",
        },
        {
            name: windowWidth < 768 ? "INVESTED" : "AMOUNT INVESTED",
            selector: (row) => `$${row.amount.toLocaleString()}`,
            sortable: true,
            width: useScrollableColumns ? "130px" : "14%",
            minWidth: useScrollableColumns ? "130px" : "auto",
        },
        {
            name: "$ RAISED",
            selector: (row) =>
                row.raised === 0 ? "-" : `$${row.raised.toLocaleString()}`,
            sortable: true,
            width: useScrollableColumns ? "110px" : "9%",
            minWidth: useScrollableColumns ? "110px" : "auto",
        },
        {
            name: "% RAISED",
            selector: (row) =>
                row.percentRaised === 0 ? "-" : `${row.percentRaised}%`,
            sortable: true,
            width: useScrollableColumns ? "110px" : "9%",
            minWidth: useScrollableColumns ? "110px" : "auto",
        },
        {
            name:
                windowWidth < 1024 ? (
                    "RECONFIRM"
                ) : (
                    <StackedHeader text="RECONFIRM INVESTMENT" />
                ),
            cell: (row) => {
                const deadline = row.reconfirmationDeadline
                    ? new Date(row.reconfirmationDeadline).getTime()
                    : NaN;
                const shouldShowButton =
                    row.reconfirmationRequired &&
                    Number.isFinite(deadline) &&
                    currentTime <= deadline;
                const isExpired =
                    row.reconfirmationExpired ||
                    (row.reconfirmationRequired &&
                        Number.isFinite(deadline) &&
                        currentTime > deadline);
                const isReconfirming =
                    reconfirmingInvestmentId === row.id;

                if (shouldShowButton) {
                    return (
                        <button
                            type="button"
                            onClick={() => handleReconfirmInvestment(row)}
                            disabled={isReconfirming}
                            className="px-3 py-2 rounded-md bg-navy text-white text-xs font-semibold hover:bg-navy-2 disabled:opacity-60"
                            title={`Reconfirm by ${new Date(
                                deadline,
                            ).toLocaleString()}`}
                        >
                            {isReconfirming
                                ? "Reconfirming..."
                                : "Reconfirm Investment"}
                        </button>
                    );
                }

                return isExpired ? (
                    <span
                        className="px-3 py-2 rounded-md bg-red-100 text-red-700 text-xs font-semibold"
                        title={`Reconfirmation deadline expired on ${new Date(
                            deadline,
                        ).toLocaleString()}`}
                    >
                        Expired
                    </span>
                ) : (
                    <span>-</span>
                );
            },
            width: useScrollableColumns ? "170px" : "15%",
            minWidth: useScrollableColumns ? "170px" : "auto",
            center: true,
        },
        {
            name:
                windowWidth < 1024 ? (
                    "DAYS"
                ) : (
                    <StackedHeader text="DAYS REMAINING" />
                ),
            selector: (row) =>
                row.daysRemaining === 0 ? "-" : row.daysRemaining,
            sortable: true,
            width: useScrollableColumns ? "100px" : "10%",
            minWidth: useScrollableColumns ? "100px" : "auto",
            conditionalCellStyles: [
                {
                    when: (row) =>
                        row.daysRemaining <= 30 && row.daysRemaining > 0,
                    style: { color: "#FF9800", fontWeight: "bold" },
                },
                {
                    when: (row) => row.daysRemaining === 0,
                    style: { color: "#F44336", fontWeight: "bold" },
                },
            ],
        },
        // {
        //     name:
        //         windowWidth < 1024 ? (
        //             "SHARES"
        //         ) : (
        //             <StackedHeader text="SHARE DISTRIBUTION" />
        //         ),
        //     cell: (row) =>
        //         row.shareLink && row.shareLink !== "#" ? (
        //             <a
        //                 href={row.shareLink}
        //                 target="_blank"
        //                 rel="noopener noreferrer"
        //                 style={{
        //                     color: "var(--navy)",
        //                     fontWeight: "bold",
        //                     textDecoration: "underline",
        //                 }}
        //             >
        //                 View
        //             </a>
        //         ) : (
        //             <span>-</span>
        //         ),
        //     width:
        //         windowWidth < 768
        //             ? "100px"
        //             : windowWidth < 1024
        //               ? "12%"
        //               : "12%",
        //     minWidth: windowWidth < 768 ? "100px" : "auto",
        // },
        {
            name:
                windowWidth < 1024 ? (
                    "STATUS"
                ) : (
                    <StackedHeader text="ORDER STATUS" />
                ),
            selector: (row) => row.orderStatus || "-",
            sortable: true,
            width: useScrollableColumns ? "120px" : "12%",
            minWidth: useScrollableColumns ? "120px" : "auto",
            cell: (row) => {
                const displayStatus = row.orderStatus || "-";
                const isRefreshing = refreshingOrderStatusId === row.id;
                const canRefresh = Boolean(row.tradeId);

                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontWeight: 500,
                            color:
                                displayStatus === "CREATED"
                                    ? "var(--navy)"
                                    : displayStatus === "Cancelled" ||
                                        displayStatus === "CANCELLED"
                                      ? "#B42318"
                                      : "inherit",
                        }}
                    >
                        <span>{displayStatus}</span>
                        {canRefresh ? (
                            <button
                                type="button"
                                onClick={() => handleRefreshOrderStatus(row)}
                                disabled={isRefreshing}
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    padding: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    color: "var(--navy)",
                                    cursor: isRefreshing ? "wait" : "pointer",
                                    opacity: isRefreshing ? 0.6 : 1,
                                }}
                                title="Refresh order status"
                            >
                                <FaSyncAlt
                                    className={isRefreshing ? "animate-spin" : ""}
                                    style={{
                                        fontSize: "0.9em",
                                    }}
                                />
                            </button>
                        ) : null}
                    </div>
                );
            },
        },
        {
            name:
                windowWidth < 1024 ? (
                    "CANCEL"
                ) : (
                    <StackedHeader text="CANCEL INVESTMENT" />
                ),
            cell: (row) => {
                const isLoading = cancellingInvestment === row.id;
                // Check cancelled field to determine if investment can be cancelled
                const canCancel = !row.cancelled;
                const isWithinTimeLimit = isWithin48Hours(row.created_at);
                const canActuallyCancel = canCancel && isWithinTimeLimit;

                return (
                    <div
                        style={{
                            color: canActuallyCancel ? "darkred" : "gray",
                            textAlign: "center",
                        }}
                    >
                        {isLoading ? (
                            <div style={{ fontSize: "0.8em" }}>
                                Cancelling...
                            </div>
                        ) : row.cancelled ? (
                            <div style={{ fontSize: "0.7em", color: "#666" }}>
                                Cancelled
                            </div>
                        ) : !isWithinTimeLimit ? (
                            <div style={{ fontSize: "0.7em", color: "#666" }}>
                                48hrs expired
                            </div>
                        ) : (
                            <FaTimes
                                style={{
                                    fontSize:
                                        windowWidth < 768 ? "1.2em" : "1em",
                                    cursor: canActuallyCancel
                                        ? "pointer"
                                        : "not-allowed",
                                    opacity: canActuallyCancel ? 1 : 0.5,
                                }}
                                onClick={
                                    canActuallyCancel
                                        ? () => showCancelConfirmation(row)
                                        : undefined
                                }
                                title={
                                    !canCancel
                                        ? "Investment already cancelled"
                                        : !isWithinTimeLimit
                                          ? "Investment can only be cancelled within 48 hours of creation"
                                          : row.tradeId
                                            ? "Cancel North Capital investment"
                                            : "Cancel manual investment"
                                }
                            />
                        )}
                    </div>
                );
            },
            width: useScrollableColumns ? "100px" : "10%",
            minWidth: useScrollableColumns ? "100px" : "auto",
            center: true,
        },
    ];

    // Filter campaigns
    const filteredInvestments = investments.filter((investment) => {
        return investment.name.toLowerCase().includes(search.toLowerCase());
    });

    // Pagination calculations
    const totalPages = Math.ceil(filteredInvestments.length / perPage);
    const paginatedData = filteredInvestments.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );

    // Custom Pagination Component
    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing{" "}
                {filteredInvestments.length === 0
                    ? 0
                    : (currentPage - 1) * perPage + 1}{" "}
                to {Math.min(currentPage * perPage, filteredInvestments.length)}{" "}
                of {filteredInvestments.length} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    return (
        <div
            className="aqi-grid-page investment-tracker-container"
            style={{
                padding: "30px",
                width: "90%",
                minHeight: "80vh",
                height: "auto",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
                overflowX: "hidden",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "20px",
                    fontSize: "large",
                    fontFamily: "large",
                }}
            >
                INVESTMENTS
            </h2>

            {/* Search and Pagination Controls */}
            <div
                className="search-controls"
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: windowWidth < 768 ? "stretch" : "center",
                    marginBottom: "20px",
                    gap: windowWidth < 768 ? "10px" : "20px",
                    flexWrap: "wrap",
                    flexDirection: windowWidth < 768 ? "column" : "row",
                }}
            >
                {/* Rows per page - Top Left */}
                <div>
                    <select
                        style={{
                            padding: "0.375rem 2rem 0.375rem 0.75rem",
                            color: "var(--navy)",
                            backgroundColor: "#fff",
                            border: "2px solid #C2D7E2",
                            borderRadius: "0.375rem",
                            fontWeight: "500",
                            appearance: "none",
                            backgroundImage:
                                "var(--navy-select-chevron)",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right 0.5rem center",
                            backgroundSize: "16px 12px",
                            transition: "all 0.2s ease",
                            cursor: "pointer",
                            outline: "none",
                        }}
                        className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        value={perPage}
                        onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        {GRID_CONFIG.pageSizeOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                </div>

                {/* Search bar - Top Right */}
                <div className="relative ml-auto">
                    <div
                        className="relative"
                        style={{
                            width: windowWidth < 768 ? "100%" : "250px",
                            position: "relative",
                        }}
                    >
                        <FaSearch
                            className="absolute left-3 top-2.5"
                            style={{ color: "var(--navy)" }}
                        />
                        <input
                            type="text"
                            placeholder="Search campaigns..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setCurrentPage(1);
                            }}
                            style={{
                                paddingLeft: "2.5rem",
                                paddingRight: "0.75rem",
                                paddingTop: "0.5rem",
                                paddingBottom: "0.5rem",
                                width: "100%",
                                borderRadius: "0.375rem",
                                border: "2px solid #C2D7E2",
                                backgroundColor: "#F5F7FA",
                                transition: "all 0.2s ease",
                                outline: "none",
                                color: "var(--navy)",
                            }}
                            className="hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        />
                    </div>
                </div>
            </div>

            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    height: windowWidth < 768 ? "auto" : "calc(100% - 120px)",
                }}
            >
                {filteredInvestments.length === 0 ? (
                    // No investments message
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: windowWidth < 768 ? "300px" : "400px",
                            textAlign: "center",
                            color: "#666",
                            padding: windowWidth < 768 ? "20px" : "40px",
                        }}
                    >
                        <div
                            style={{
                                marginBottom: "20px",
                                fontSize: windowWidth < 768 ? "16px" : "18px",
                                fontWeight: "bold",
                            }}
                        >
                            No Investments Yet
                        </div>
                        <div
                            style={{
                                marginBottom: "20px",
                                lineHeight: "1.5",
                                fontSize: windowWidth < 768 ? "14px" : "16px",
                            }}
                        >
                            You haven&apos;t made any investments yet.
                            <br />
                            Check out our available campaigns and start
                            investing!
                        </div>
                        <a
                            href="/investors"
                            style={{
                                display: "inline-block",
                                padding:
                                    windowWidth < 768
                                        ? "14px 28px"
                                        : "12px 24px",
                                backgroundColor: "var(--navy)",
                                color: "white",
                                textDecoration: "none",
                                borderRadius: "6px",
                                fontWeight: "bold",
                                fontSize: windowWidth < 768 ? "16px" : "14px",
                                transition: "background-color 0.3s ease",
                                width: windowWidth < 768 ? "100%" : "auto",
                                maxWidth: windowWidth < 768 ? "300px" : "none",
                                textAlign: "center",
                            }}
                            onMouseOver={(e) =>
                                (e.target.style.backgroundColor = "var(--navy-2)")
                            }
                            onMouseOut={(e) =>
                                (e.target.style.backgroundColor = "var(--navy)")
                            }
                        >
                            View Available Campaigns
                        </a>
                    </div>
                ) : (
                    <>
                        <div
                            style={{
                                flex: 1,
                                overflow: "auto",
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                backgroundColor: "white",
                            }}
                        >
                            <DataTable
                                columns={columns}
                                data={paginatedData}
                                customStyles={customStyles}
                                progressPending={false}
                                pagination={false}
                                highlightOnHover
                                pointerOnHover
                                responsive={false}
                                striped
                                fixedHeader={windowWidth >= 768}
                                fixedHeaderScrollHeight={
                                    windowWidth < 768 ? "none" : "400px"
                                }
                                noDataComponent={
                                    <div
                                        style={{
                                            padding: "20px",
                                            textAlign: "center",
                                            color: "#666",
                                        }}
                                    >
                                        No investments found
                                    </div>
                                }
                                style={{
                                    minWidth: useScrollableColumns
                                        ? scrollableTableWidth
                                        : "100%",
                                    width: useScrollableColumns
                                        ? scrollableTableWidth
                                        : "100%",
                                }}
                            />
                        </div>

                        {/* Custom Pagination UI */}
                        <CustomPagination />

                        {/* Back to Available Campaigns */}
                        <div
                            style={{
                                textAlign: "center",
                                marginTop: windowWidth < 768 ? "15px" : "20px",
                                padding: windowWidth < 768 ? "10px" : "15px",
                            }}
                        >
                            <a
                                href="/investors"
                                style={{
                                    color: "var(--navy)",
                                    textDecoration: "none",
                                    fontSize:
                                        windowWidth < 768 ? "14px" : "14px",
                                    fontWeight: "bold",
                                    padding:
                                        windowWidth < 768
                                            ? "10px 20px"
                                            : "8px 16px",
                                    border: "1px solid var(--navy)",
                                    borderRadius: "6px",
                                    transition: "all 0.3s ease",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    width: windowWidth < 768 ? "auto" : "auto",
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.backgroundColor = "var(--navy)";
                                    e.target.style.color = "white";
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.backgroundColor =
                                        "transparent";
                                    e.target.style.color = "var(--navy)";
                                }}
                            >
                                View Available Campaigns
                                <FaArrowRight size={14} />
                            </a>
                        </div>
                    </>
                )}
            </div>

            {/* Cancel Investment Confirmation Dialog */}
            {showCancelDialog && selectedInvestment && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 1000,
                    }}
                    onClick={closeCancelDialog}
                >
                    <div
                        style={{
                            backgroundColor: "white",
                            padding: "30px",
                            borderRadius: "10px",
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                            maxWidth: "500px",
                            width: "90%",
                            textAlign: "center",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h1 style={{ color: "var(--navy)", marginBottom: "20px" }}>
                            CONFIRM INVESTMENT CANCELLATION
                        </h1>
                        <p style={{ marginBottom: "20px", lineHeight: "1.5" }}>
                            Are you sure you want to cancel your investment in{" "}
                            <strong>{selectedInvestment.name}</strong>?
                        </p>
                        <p
                            style={{
                                marginBottom: "20px",
                                color: "#666",
                                fontSize: "14px",
                            }}
                        >
                            Investment Amount:{" "}
                            <strong>
                                ${selectedInvestment.amount.toLocaleString()}
                            </strong>
                        </p>

                        {/* Cancellation Reason Input */}
                        <div
                            style={{ marginBottom: "20px", textAlign: "left" }}
                        >
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                    color: "#333",
                                }}
                            >
                                Cancellation Reason{" "}
                                <span style={{ color: "red" }}>*</span>:
                            </label>
                            <textarea
                                value={cancellationReason}
                                onChange={(e) =>
                                    setCancellationReason(e.target.value)
                                }
                                placeholder="Please provide the reason for cancellation..."
                                required
                                style={{
                                    width: "100%",
                                    height: "60px",
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    resize: "vertical",
                                    fontFamily: "inherit",
                                }}
                                maxLength="300"
                            />
                            <small style={{ color: "#666", fontSize: "12px" }}>
                                {cancellationReason.length}/300 characters
                            </small>
                        </div>

                        {/* Cancellation Notes Input */}
                        <div
                            style={{ marginBottom: "20px", textAlign: "left" }}
                        >
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "bold",
                                    color: "#333",
                                }}
                            >
                                Additional Notes{" "}
                                <span style={{ color: "red" }}>*</span>:
                            </label>
                            <textarea
                                value={cancellationNotes}
                                onChange={(e) =>
                                    setCancellationNotes(e.target.value)
                                }
                                placeholder="Please provide any additional details about the cancellation..."
                                required
                                style={{
                                    width: "100%",
                                    height: "80px",
                                    padding: "10px",
                                    border: "1px solid #ddd",
                                    borderRadius: "4px",
                                    fontSize: "14px",
                                    resize: "vertical",
                                    fontFamily: "inherit",
                                }}
                                maxLength="500"
                            />
                            <small style={{ color: "#666", fontSize: "12px" }}>
                                {cancellationNotes.length}/500 characters
                            </small>
                        </div>

                        {/* Warning Message */}
                        <div
                            style={{
                                marginBottom: "20px",
                                padding: "15px",
                                backgroundColor: "#fff3cd",
                                border: "1px solid #ffeaa7",
                                borderRadius: "6px",
                                textAlign: "center",
                            }}
                        >
                            <p
                                style={{
                                    margin: "0",
                                    color: "#856404",
                                    fontWeight: "bold",
                                    fontSize: "14px",
                                }}
                            >
                                ⚠️ This action cannot be undone!
                            </p>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "15px",
                                justifyContent: "center",
                            }}
                        >
                            <button
                                onClick={closeCancelDialog}
                                style={{
                                    padding: "12px 24px",
                                    backgroundColor: "#95a5a6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontWeight: "bold",
                                    transition: "background-color 0.3s ease",
                                }}
                                onMouseOver={(e) =>
                                    (e.target.style.backgroundColor = "#7f8c8d")
                                }
                                onMouseOut={(e) =>
                                    (e.target.style.backgroundColor = "#95a5a6")
                                }
                            >
                                Keep Investment
                            </button>
                            <button
                                onClick={confirmCancelInvestment}
                                disabled={
                                    cancellingInvestment ===
                                        selectedInvestment.id ||
                                    !cancellationReason.trim() ||
                                    !cancellationNotes.trim()
                                }
                                style={{
                                    padding: "12px 24px",
                                    backgroundColor:
                                        cancellingInvestment ===
                                            selectedInvestment.id ||
                                        !cancellationReason.trim() ||
                                        !cancellationNotes.trim()
                                            ? "#95a5a6"
                                            : "var(--navy)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor:
                                        cancellingInvestment ===
                                            selectedInvestment.id ||
                                        !cancellationReason.trim() ||
                                        !cancellationNotes.trim()
                                            ? "not-allowed"
                                            : "pointer",
                                    fontWeight: "bold",
                                    opacity:
                                        cancellingInvestment ===
                                            selectedInvestment.id ||
                                        !cancellationReason.trim() ||
                                        !cancellationNotes.trim()
                                            ? 0.7
                                            : 1,
                                    transition: "background-color 0.3s ease",
                                }}
                                onMouseOver={(e) => {
                                    if(
                                        cancellingInvestment !==
                                            selectedInvestment.id &&
                                        cancellationReason.trim() &&
                                        cancellationNotes.trim()
                                    ) {
                                        e.target.style.backgroundColor =
                                            "var(--navy)";
                                    }
                                }}
                                onMouseOut={(e) => {
                                    if(
                                        cancellingInvestment !==
                                            selectedInvestment.id &&
                                        cancellationReason.trim() &&
                                        cancellationNotes.trim()
                                    ) {
                                        e.target.style.backgroundColor =
                                            "var(--navy)";
                                    }
                                }}
                            >
                                {cancellingInvestment === selectedInvestment.id
                                    ? "Cancelling..."
                                    : !cancellationReason.trim() ||
                                        !cancellationNotes.trim()
                                      ? "Fill Required Fields"
                                      : "Cancel Investment"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InvestmentTable;
